import {
  ALPHABET_WRITING_PLAYBACK_CONTRACT,
  ALPHABET_WRITING_TEMPLATE_ID,
  type AlphabetWritingWordLayoutPolicyV1,
} from '@crabs-ai/alphabet-writing-runtime/contracts';
import { BaseItem, CanHaveAnimation, CanHaveRotation } from '../shared';
import { EditorItemType } from '@/features/remotion-editor/editor/items/item-type';
import type { AuthoringWritingGuideStyle } from '@/shared/lib/writing-guide/authoring-style';

export const AUTHORING_WRITING_GUIDE_TEMPLATE_ID = ALPHABET_WRITING_TEMPLATE_ID;
export const AUTHORING_WRITING_GUIDE_PLAYBACK_CONTRACT =
  ALPHABET_WRITING_PLAYBACK_CONTRACT;
export const WORD_CHANT_TEMPLATE_ID = 'word-chant-v1';

export type AuthoringWritingGuideSpecV1 = {
  version: 'alphabet.authoring_render_spec.v1';
  word: string;
  runtime: Record<string, unknown>;
  style?: Record<string, unknown>;
  layoutOverride?: unknown;
  strokeTimeline?: unknown[];
  timelineOptions?: Record<string, unknown>;
};

export type AuthoringWordLayoutPolicyV1 = AlphabetWritingWordLayoutPolicyV1;

export type AuthoringWritingGuideSpecV2 = {
  version: 'alphabet.authoring_render_spec.v2';
  word: string;
  runtime: Record<string, unknown>;
  style?: Record<string, unknown>;
  layoutPolicy: AuthoringWordLayoutPolicyV1;
  layoutOverride?: unknown;
  strokeTimeline?: unknown[];
  timelineOptions?: Record<string, unknown>;
};

export type CodeItem = BaseItem &
  CanHaveAnimation &
  CanHaveRotation & {
    type: Extract<EditorItemType, 'code'>;
    code: string;
    codeClock?: {startInSeconds:number;endInSeconds:number};
    compiledError?: string;
    title?: string;
    templateId?: string;
    playbackContract?: string;
    alphabetCase?: string;
    rendererSourceHash?: string;
    rendererAssetPath?: string;
    runtimeSourceHash?: string;
    rendererVersion?: string;
    authoringStyle?: AuthoringWritingGuideStyle;
    authoringSpec?: AuthoringWritingGuideSpecV1 | AuthoringWritingGuideSpecV2;
    wordChantSpec?: unknown;
    fps?: number;
    durationSec?: number;
    fadeInDurationInSeconds: number;
    fadeOutDurationInSeconds: number;
    /**
     * 렌더 영역 모드.
     * - 'fullscreen' (기본): 코드가 AbsoluteFill 로 캔버스 전체를 차지. item.left/top 만 transform 으로 적용
     * - 'bounded': item.left/top/width/height 에 맞춰 wrap div 크기 제한 + overflow hidden. drag/resize 가능
     */
    boundingMode?: 'fullscreen' | 'bounded';
    /**
     * GTS_STEP M 씬: chunk.code 안의 `timingOverrides?.['<element_id>'] ?? <default>` 가
     * TTS 합성 후 actual block start frame 으로 동기되도록 주입할 element timing 맵.
     * Lambda export 와 미리보기 양쪽이 동일한 값을 받아야 element 등장 속도가 일치한다.
     * 미리보기 컴포넌트(`ChunkLayer`/`MSceneRemotionPreview`)는 별도 prop 으로 forward 하지만,
     * Remotion site 의 `CodeLayer` 는 이 필드를 통해 받아 `<Component timingOverrides={...} />` 로 전달.
     */
    timingOverrides?: Record<string, number> | null;
    /**
     * GTS_STEP renderer timing SSOT. New generated scene components may read
     * `timeline.scenes[].segments/elementCues` directly and use timingOverrides
     * only as a compatibility fallback.
     */
    timeline?: unknown;
    codeActiveField?: string | null;
    codeActiveFields?: string[] | null;
    shinhanPdfActiveField?: string | null;
  };

export function isAuthoringWritingGuideCodeItem(
  item: Pick<CodeItem, 'templateId'>,
): boolean {
  return item.templateId === AUTHORING_WRITING_GUIDE_TEMPLATE_ID;
}

export function isWordChantCodeItem(
  item: Pick<CodeItem, 'templateId'>,
): boolean {
  return item.templateId === WORD_CHANT_TEMPLATE_ID;
}
